
import { useState } from 'react';
import { toast } from '@/hooks/use-toast';
import { useBulkCreateDetailedProducts } from '@/hooks/use-product-management';
import * as XLSX from 'xlsx';
import { createTestExcelFile } from '@/utils/excel/templates/testFiles';

export const useUploadActions = (onUploadComplete: () => void, handleReset: () => void) => {
  const bulkCreateProducts = useBulkCreateDetailedProducts();
  
  const handleUpload = async (previewData: Array<{
    name: string;
    category: string | null;
    description: string | null;
    unit_of_measure?: string;
  }>) => {
    if (previewData.length === 0) {
      toast({
        title: 'No products to upload',
        description: 'Please select a file with valid product data first.',
        variant: 'default',
      });
      return;
    }
    
    try {
      console.log('Starting upload of products:', previewData);
      const result = await bulkCreateProducts.mutateAsync(previewData);
      console.log('Upload result:', result);
      
      // Handle various result scenarios
      if (result.updated && result.updated > 0) {
        if (result.added > 0) {
          toast({
            title: 'Products Uploaded',
            description: `${result.added} products added and ${result.updated} existing products updated.`,
          });
        } else {
          toast({
            title: 'Products Updated',
            description: `${result.updated} existing products were updated with new information.`,
          });
        }
      } else if (result.added > 0) {
        toast({
          title: 'Products Uploaded',
          description: `${result.added} products were added successfully.`,
        });
      } else if (result.skipped > 0 && result.added === 0 && result.updated === 0) {
        toast({
          title: 'Products Already Exist',
          description: 'All products already exist in the database with the same units.',
          variant: 'default',
        });
      } else {
        toast({
          title: 'Products Processed',
          description: result.message || 'Products have been processed.',
        });
      }
      
      // Reset the form
      handleReset();
      
      // Notify parent component
      onUploadComplete();
    } catch (error: any) {
      console.error('Failed to upload products:', error);
      
      // Provide more specific error message for duplicate products
      let errorMessage = 'Failed to upload products. Please try again.';
      
      if (error?.message) {
        errorMessage = error.message;
      }
      
      // Extract the duplicate product name from the error details
      if (error?.code === '23505') {
        const match = error?.details?.match(/Key \(name\)=\((.*?)\)/i);
        const productName = match ? match[1] : 'Unknown';
        errorMessage = `Product "${productName}" already exists. Please update your file and try again.`;
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const downloadTemplate = () => {
    // Create a template workbook with the specific format requested, including Amharic examples
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ['product_name', 'measuring_unit', 'category_name', 'description'],
      ['Tomato', 'kg', 'Vegetables', 'Fresh organic tomatoes'],
      ['አቦካዶ', 'kg', 'ፍራፍሬዎች', 'Fresh avocados'],
      ['Rice', 'bag', 'Grains', 'Premium long grain rice'],
      ['ቡላ', 'kg', 'አኩሪ አተር', 'Ethiopian specialty']
    ]);
    
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    
    // Create a blob and download
    XLSX.writeFile(wb, 'product_template.xlsx');
  };
  
  const downloadTestFile = () => {
    // Create a test file with multilingual data including Amharic
    const blob = createTestExcelFile();
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'test_product_file.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast({
      title: 'Test File Downloaded',
      description: 'A sample Excel file with valid product data (including Amharic examples) has been downloaded. Try uploading this file.',
    });
  };

  return {
    bulkCreateProducts,
    handleUpload,
    downloadTemplate,
    downloadTestFile
  };
};
